import { noop } from 'lodash';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Text } from '~/eds/src/components/Text/Text';
import { User } from '~/eds/src/components/User/User';
import { Feedback } from '~/eds/src/modules/Feedback/Feedback';
import { Nullable } from '~/eds/src/types/Nullable';
import { User as EdsUser } from '~/eds/src/types/User';
import { UserAction } from '~/eds/src/types/UserAction';
import { typedMemo } from '~/eds/src/utils/typedMemo';

import { FeedbackDetails, Message as MessageType } from '../types/chat';
import { BlinkingCursor } from './BlinkingCursor';
import { CHOICE_DISPLAY_LENGTH, MESSAGE_ACTIONS_CLASSNAME } from './constants';
import { getUserTheme } from './utils';

interface Props {
  message: MessageType;
  user: EdsUser;
  onChoose: (message: MessageType, choice: string) => void;
  onSelectSource: (message: MessageType, sourceIndex: number) => void;
  onSubmitFeedback: (
    message: MessageType,
    feedbackDetails: FeedbackDetails,
  ) => void;
  disableChoices?: boolean;
  index?: number;
  isReplying?: boolean;
  messageActions?: UserAction[];
  shouldDisableSources?: (message: MessageType) => boolean;
}

export const Message = typedMemo(
  ({
    disableChoices,
    index = -1,
    isReplying,
    message,
    messageActions = [],
    onChoose,
    onSelectSource,
    onSubmitFeedback,
    shouldDisableSources,
    user,
  }: Props) => {
    const intl = useIntl();
    const handleSubmitFeedback = (value: Nullable<boolean>) => {
      onSubmitFeedback(message, { value });
    };
    const { choices = [], sources = [], text, timestamp, userType } = message;

    const { bg } = getUserTheme(userType);

    const messageChoices = useMemo(
      () =>
        (message.choices ?? []).map((choice) => {
          /** TODO - `Button` should handle this truncation internally after design proposes the display length limit */
          const isLongText = choice.length > CHOICE_DISPLAY_LENGTH;
          return {
            disabled: disableChoices,
            level: 'secondary' as const,
            text: isLongText
              ? choice.substring(0, CHOICE_DISPLAY_LENGTH) + '…'
              : choice,
            tooltip: isLongText ? choice : undefined,
            onClick: () => onChoose(message, choice),
          };
        }),
      [disableChoices, message],
    );

    const sourcesDisabled = useMemo(
      () => shouldDisableSources?.(message) ?? false,
      [shouldDisableSources],
    );
    const messageSources = useMemo(
      () =>
        (message.sources ?? []).map((_source, sourceIndex) => ({
          level: 'secondary' as const,
          text: `${sourceIndex + 1}`,
          tooltip: sourcesDisabled
            ? intl.formatMessage({
                id: 'DeprecatedMessage.sourceNotSupportedTooltip',
                defaultMessage:
                  'Source highlights are not supported for this document.',
                description: 'Tooltip when source highlights are not supported',
              })
            : intl.formatMessage(
                {
                  id: 'DeprecatedMessage.viewSourceTooltip',
                  defaultMessage: 'View source {sourceNumber}',
                  description:
                    'Tooltip for viewing a source. Example: sourceNumber = 1, 2, 3',
                },
                {
                  sourceNumber: sourceIndex + 1,
                },
              ),
          onClick: () => onSelectSource(message, sourceIndex),
          disabled: sourcesDisabled,
        })),
      [message, sourcesDisabled],
    );

    const userOptions = {
      size: 'l' as const,
      date: timestamp,
    };

    return (
      <Layout
        px={4}
        py={6}
        spacing={4}
        bg={bg}
        className={userType}
        styles={componentStyles.message}
        onMouseEnter={noop}
      >
        <User mode="avatar" user={user} options={userOptions} />
        <Layout
          direction="column"
          flex="auto"
          justify="center"
          minW={0}
          position="relative"
          spacing={2}
        >
          <Markdown text={text} />
          {choices && <Actions wrap actions={messageChoices} size="s" />}
          {sources.length > 0 && (
            <Layout align="center" spacing={1}>
              <Text preset="help">
                {intl.formatMessage({
                  id: 'DeprecatedMessage.basedOnLabel',
                  defaultMessage: 'Based on:',
                  description: 'Label for sources section',
                })}{' '}
              </Text>
              <Actions wrap actions={messageSources} size="s" />
            </Layout>
          )}
          {isReplying && <BlinkingCursor />}
        </Layout>
        {index > 0 && (
          <Layout
            bg="background"
            borderRadius={4}
            className={MESSAGE_ACTIONS_CLASSNAME}
            styles={componentStyles.actions}
          >
            <Actions actions={messageActions} />
            {Boolean(messageActions.length) && (
              <Feedback
                value={message.feedbackDetails?.value ?? null}
                onChange={handleSubmitFeedback}
              />
            )}
          </Layout>
        )}
      </Layout>
    );
  },
);

const componentStyles = {
  actions: {
    boxShadow: 'raised',
    bottom: 0,
    position: 'absolute',
    right: 4,
    transform: 'translateY(-10%)',
    visibility: 'hidden',
  },
  message: {
    position: 'relative',
    [`&:hover .${MESSAGE_ACTIONS_CLASSNAME}`]: {
      visibility: 'visible',
    },
    [`&.current:hover + .other .${MESSAGE_ACTIONS_CLASSNAME}`]: {
      visibility: 'visible',
    },
  },
};
